import type { UseInfiniteQueryResult } from 'react-query';
import { useInfiniteQuery } from 'react-query';

import type { PaginatedOrganizationJobsResponse } from '../types';

type UseOrganizationJobsInput = {
  assignedStatus?: 'open' | 'assigned';
  jobType?: 'interest' | 'urgent' | 'personal' | undefined;
  selectedFreelancers?: Array<number>;
  selectedLocations?: Array<number>;
  selectedOrganizations?: Array<number>;
  pageSize?: number;
  page?: number;
  fromDate: string;
  toDate: string;
};

const usePaginatedOrganizationJobs = (
  params: UseOrganizationJobsInput,
): UseInfiniteQueryResult<PaginatedOrganizationJobsResponse, string> => {
  const infiniteQuery = useInfiniteQuery<
    PaginatedOrganizationJobsResponse,
    string
  >(
    [
      'paginated-organization-jobs',
      params.selectedFreelancers,
      params.selectedLocations,
      params.selectedOrganizations,
      params.pageSize,
      params.fromDate,
      params.toDate,
      params.page,
      params.assignedStatus,
      params.jobType,
    ],
    async ({ pageParam = 1 }) => {
      const query = new URLSearchParams();

      if (params.pageSize) {
        query.append('page_size', params.pageSize.toString());
      }
      query.append('page', params.page?.toString() ?? pageParam);

      if (
        typeof params?.selectedFreelancers === 'object' &&
        params?.selectedFreelancers?.length > 0
      ) {
        params?.selectedFreelancers.forEach((item) =>
          query.append('assigned_to', item.toString()),
        );
      }

      if (
        typeof params?.selectedOrganizations === 'object' &&
        params?.selectedOrganizations?.length > 0
      ) {
        params?.selectedOrganizations.forEach((item) =>
          query.append('organisation', item.toString()),
        );
      }

      if (
        typeof params?.selectedLocations === 'object' &&
        params?.selectedLocations?.length > 0
      ) {
        params?.selectedLocations.forEach((item) =>
          query.append('location', item.toString()),
        );
      }

      if (params.assignedStatus) {
        query.append('assigned_status', params.assignedStatus);
      }

      if (params.jobType) {
        query.append('job_type', params.jobType);
      }

      query.append('pagination', 'True');
      query.append('from_date', params.fromDate);
      query.append('to_date', params.toDate);

      const path = `/api/organisation/jobs?${new URLSearchParams(query)}`;

      const response = await fetch(path);
      const body = await response.json();
      if (response.ok)
        return { ...body, pageNumber: parseInt(query.get('page') ?? '1', 10) };
      throw new Error('Something went wrong');
    },
    {
      getNextPageParam: (lastPage, allPages): number | undefined => {
        if (lastPage.next) {
          const searchQuery = lastPage.next.split('?')[1];
          const searchParams = new URLSearchParams(searchQuery);
          const nextpage = searchParams.get('page') ?? '1';
          const nextPageNumber = parseInt(nextpage, 10);

          const isPageExist = allPages.find(
            (page) => page.pageNumber === nextPageNumber,
          );

          if (isPageExist) return undefined;

          return nextPageNumber;
        }
        return undefined;
      },
      getPreviousPageParam: (firstPage, allPages): number | undefined => {
        if (firstPage.previous) {
          const searchQuery = firstPage.previous.split('?')[1];
          const searchParams = new URLSearchParams(searchQuery);
          const previousPage = searchParams.get('page') ?? '1';
          const previousPageNumber = parseInt(previousPage, 10);
          const isPageExist = allPages.filter(
            (page) => page.pageNumber === previousPageNumber,
          );

          if (isPageExist) return undefined;

          return previousPageNumber;
        }
        return undefined;
      },
      keepPreviousData: true,
    },
  );
  return infiniteQuery;
};

export default usePaginatedOrganizationJobs;
